import { dateStringLongMonth, dateStringLongMonthDayYear } from 'mewe/utils/datetime-utils';
import Component from '@glimmer/component';
import { computed, action } from '@ember/object';

import PS from 'mewe/utils/pubsub';
import { StickyController } from 'mewe/utils/elements-utils';
import Storage from 'mewe/shared/storage';
import dispatcher from 'mewe/dispatcher';
import { ds } from 'mewe/stores/ds';
import { fetchUsersCounters } from 'mewe/fetchers/fetch-followers';
import { inject as service } from '@ember/service';
import { showNewToMeWePopup } from 'mewe/utils/dialogs-common';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import FollowingApi from 'mewe/api/following-api';
import { addObserver, removeObserver } from '@ember/object/observers';
import PurchasesStore from 'mewe/stores/purchases-store';
import FunctionalUtils from 'mewe/shared/functional-utils';
import ChatStore from 'mewe/stores/chat-store';
import ThreadFactory from 'mewe/stores/models/thread-model';
import CurrentUserStore from 'mewe/stores/current-user-store';
import ReputationApi from 'mewe/api/reputation-api';
import { TellerType } from 'mewe/constants';
import { toTeller } from 'mewe/fetchers/fetch-stories';

export default class ProfileMain extends Component {
  @service chat;
  @service tasks;
  @service account;
  @service router;
  @service dynamicDialogs;
  @service features;

  @tracked showSettingsDropdown;
  @tracked showOptionsDropdown;
  @tracked dropdownMenuOpenedAvatar;
  @tracked dropdownMenuOpenedCover;
  @tracked avatarTask;
  @tracked coverTask;
  @tracked dialogTab;
  @tracked usersCounters = ds.usersCounters;
  @tracked mutualConnectionsResponse;
  @tracked callDropdownOpened = false;
  @tracked reputationPoints = 0;

  @computed('mutualContactsCount.length', 'mutualContacts')
  get isUserHidesFollowers() {
    return this.mutualContactsCount && this.mutualContacts.length === 0;
  }

  get contact() {
    return this.args.contact;
  }

  get badges() {
    return this.args.contact.badges;
  }

  get isPrivate() {
    return this.contact.public === false;
  }

  avatarDialogTitles = {
    choosePhoto: __('Choose Profile Photo'),
    cropPhoto: __('Edit Profile Photo'),
  };

  coverDialogTitles = {
    choosePhoto: __('Choose Background Photo'),
    cropPhoto: __('Edit Background Photo'),
  };

  constructor() {
    super(...arguments);
    this.avatarTask = this.tasks.getTask('photo.avatar.contacts');
    this.coverTask = this.tasks.getTask('photo.cover.contacts');
    this.selectPhotoAvatarBind = this.selectPhotoAvatar.bind(this);
    this.selectPhotoCoverBind = this.selectPhotoCover.bind(this);
    this.userBlockedHandlerBind = this.userBlockedHandler.bind(this);
    PS.Sub('user.blocked', this.userBlockedHandlerBind);
    addObserver(this, 'args.uuid', this.loadMutualConnections.perform);
    this.loadMutualConnections.perform();
  }

  @task
  *loadMutualConnections() {
    // don't try to make request on public profile
    if (!this.args.isMyProfile && !this.args.isPublicProfilePage) {
      this.mutualConnectionsResponse = yield FollowingApi.getFollowersOf({ userIds: this.args.uuid });
    } else {
      this.mutualConnectionsResponse = null;
    }
  }

  @computed('mutualConnectionsResponse')
  get mutualContactsCount() {
    return this.mutualConnectionsResponse?.results?.[0]?.total || 0;
  }

  @computed('mutualConnectionsResponse')
  get mutualContacts() {
    return this.mutualConnectionsResponse?.users || [];
  }

  @computed('counters.requestsReceived')
  get requestsReceivedOverOneHundred() {
    if (this.usersCounters?.requestsReceived >= 100) {
      return true;
    } else {
      return false;
    }
  }

  get memberSinceText() {
    if (this.contact.registeredAt) {
      return __('Member since {date}', {
        date: dateStringLongMonth(this.contact.registeredAt, this.account.activeUser.locale),
      });
    } else {
      return '';
    }
  }

  get memberSinceTitleText() {
    if (this.contact.registeredAt) {
      return __('Joined {date}', {
        date: dateStringLongMonthDayYear(this.contact.registeredAt, this.account.activeUser.locale),
      });
    } else {
      return '';
    }
  }

  get showQuestStatus() {
    return this.features.isEnabled('web_is_gamification_enabled') && this.args.questState?.isLoaded && this.contact.isMe;
  }

  @computed('isUserFollowedByMe', 'isMyProfile')
  get allowNsfwContent() {
    if (this.isUserFollowedByMe) {
      return true;
    }
    if (this.isMyProfile) {
      return true;
    }
    const isNsfwAllowed = Storage.get(Storage.keys.allowNsfwContentSetting);
    if (isNsfwAllowed) {
      return true;
    }
    return false;
  }

  @computed('account.activeUser.id', 'contact.id', 'args.uuid')
  get isMyProfile() {
    return this.contact && this.contact.id === this.account.activeUser.id;
  }

  @computed('args.entries.items.length')
  get hasEntries() {
    return this.args.entries?.items?.length > 0;
  }

  @computed('args.entries.items.length')
  get hasNewEntries() {
    return this.args.entries?.items?.filterBy('isNew', true).length > 0;
  }

  @computed('mutualContacts.@each.publicLinkId', 'mutualContactsCount')
  get followersTextPartOne() {
    let handle = this.mutualContacts[0]?.publicLinkId;
    if (this.mutualContactsCount === 1) {
      return __(`Followed by <strong>{handle}</strong>.`, { handle });
    } else if (this.mutualContactsCount >= 2) {
      return __(`Followed by <strong>{handle}</strong>`, { handle });
    }
  }

  @computed('mutualContacts.@each.publicLinkId', 'mutualContactsCount')
  get followersTextPartTwo() {
    let handle = this.mutualContacts[0]?.publicLinkId;
    let count = this.mutualContactsCount - 1;
    if (this.mutualContactsCount === 2) {
      return __(` and <strong>{count} other</strong>.`, { handle, count });
    } else if (this.mutualContactsCount >= 2) {
      return __(` and <strong>{count} others</strong>.`, { handle, count });
    }
  }

  get canShowInfo() {
    if (this.args.flagNewProfiles) return false;
    return this.contact.isMe || this.contact.public || this.contact.following;
  }

  get canShowOptions() {
    return !this.contact.isMe;
  }

  get canShowSettings() {
    return this.contact.isMe;
  }

  get canShowShareProfile() {
    return this.contact.isMe;
  }

  get canChat() {
    return this.contact.canChat && !this.contact.isMe;
  }

  get isFollowingMe() {
    return this.contact.follower;
  }

  get isUserFollowedByMe() {
    return this.contact.following;
  }

  get canSeeFollowers() {
    return (this.contact.following && !this.contact.hideFollowers) || this.contact.public || this.isMyProfile;
  }

  get hasPremium() {
    return PurchasesStore.getState().hasPremium;
  }

  get isReputationEnabled() {
    return this.features.isEnabled('reputation');
  }

  get showReputationBanner() {
    return this.isReputationEnabled && this.contact.isMe && this.contact.public;
  }

  userBlockedHandler(data) {
    if (data.userId === this.contact.id) {
      this.router.transitionTo('app.myworld');
    }
  }

  openJournalsDialog() {
    const tellerData = toTeller({
      id: this.contact.id,
      storytellerType: TellerType.USER,
    });
    tellerData.userStoryteller = this.contact; // contact is EmberObject, can't be serialized

    const params = {
      tellerData,
      context: 'profile_feed',
    };

    this.dynamicDialogs.openDialog('stories-dialog', params);
  }

  @action
  removeFollower() {
    dispatcher.dispatch('contact', 'removeFollower', this.contact);
    this.showOptionsDropdown = false;
  }

  @action
  shareProfile() {
    this.showOptionsDropdown = false;
    this.dynamicDialogs.openDialog('quick-share-profile-dialog', {
      dialogId: 'quick-share-profile-dialog',
      user: this.contact,
    });
  }

  @action
  inviteUserToGroup() {
    dispatcher.dispatch('contact', 'addUserToGroup', this.contact);
  }

  @action
  blockUser() {
    dispatcher.dispatch('contact', 'blockUser', this.contact, () => this.router.transitionTo('app.myworld'));
  }

  @action
  openFollowingDialog(tab) {
    if (this.args.isPublicProfilePage) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    if (this.canSeeFollowers) {
      this.dialogTab = tab;
      this.openFollowerDialog();
    }
  }

  @action
  openMutualDialog() {
    this.dialogTab = 'mutual';
    this.openFollowerDialog();
  }

  @action
  displayopenFollowerDialogPopup() {
    if (this.args.isPublicProfilePage) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    this.openFollowerDialog();
  }

  @action
  showNewToMeWePopup() {
    this.args.isPublicProfilePage && showNewToMeWePopup(this.dynamicDialogs);
  }

  @action
  reportUser() {
    dispatcher.dispatch('contact', 'reportUser', this.contact);
  }

  @action
  openAvatarPhoto() {
    if (this.args.isPublicProfilePage) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    let photoUrl = this.contact._links.avatar.href;

    this.dynamicDialogs.openDialog('media-dialog', {
      photoUrl: photoUrl,
      mediaType: 'photoUrl',
      allowMultipleInstances: true,
    });
  }

  @action
  openProfileStories() {
    if (this.args.isPublicProfilePage) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    this.openJournalsDialog();
  }

  @action
  openCoverPhoto() {
    if (this.args.isPublicProfilePage) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    let photoUrl = this.contact._links.cover.href;

    this.dynamicDialogs.openDialog('media-dialog', {
      photoUrl: photoUrl,
      mediaType: 'photoUrl',
      allowMultipleInstances: true,
    });
  }

  selectPhotoAvatar(blob, params) {
    dispatcher.dispatch('contact', 'setAvatar', blob, params, this.avatarTask);
  }

  selectPhotoCover(blob, params) {
    dispatcher.dispatch('contact', 'setPublicCover', blob, params, this.coverTask);
  }

  @action
  opendEditDropdownAvatar() {
    this.dropdownMenuOpenedAvatar = true;
  }

  @action
  closeEditDropdownAvatar() {
    this.dropdownMenuOpenedAvatar = false;
  }

  @action
  opendEditDropdownCover() {
    this.dropdownMenuOpenedCover = true;
  }

  @action
  closeEditDropdownCover() {
    this.dropdownMenuOpenedCover = false;
  }

  @action
  openChat() {
    this.chat.openThreadByParticipants([this.contact], { doNotClose: true });
  }

  @action
  showOptions(value) {
    this.showOptionsDropdown = typeof value === 'boolean' ? !value : !this.showOptionsDropdown;
  }

  @action
  showSettings() {
    if (this.isMyProfile) {
      this.router.transitionTo('app.settings.privacy');
      return;
    }
  }

  @action
  openFollowerDialog() {
    this.dynamicDialogs.openDialog('followers-dialog', {
      uuid: this.args.uuid,
      currentTab: this.dialogTab,
      title: this.contact.name,
      isPublicProfile: this.contact.public,
      contactId: this.contact.id,
    });
  }

  @action
  async onInsert(element) {
    this.stickyController = new StickyController({ element: element });

    if (!this.args.isPublicProfilePage) {
      fetchUsersCounters();
    }

    if (this.isReputationEnabled && this.contact.isMe && this.contact.public) {
      const reputation = await ReputationApi.getReputation(this.contact.id);
      this.reputationPoints = reputation.points;
    }
  }

  @action
  async onUpdate() {
    if (this.isReputationEnabled && this.contact.isMe && this.contact.public) {
      const reputation = await ReputationApi.getReputation(this.contact.id);
      this.reputationPoints = reputation.points;
    }
  }

  @action
  onDestroy() {
    PS.Unsub('user.blocked', this.userBlockedHandlerBind);
    removeObserver(this, 'args.uuid', this.loadMutualConnections.perform);
  }

  @action
  stickyHeightUpdate(isInsert, element) {
    this.stickyController.setExtraHeight(element.clientHeight, isInsert);
  }

  @action
  handleCallClick() {
    if (!this.hasPremium) {
      this.dynamicDialogs.openDialog('premium-feature-dialog', {
        context: 'profile_call',
      });
      return;
    }

    this.callDropdownOpened = true;
  }

  @action
  toggleCallDropdown() {
    this.callDropdownOpened = !this.callDropdownOpened;
  }

  @action
  async startCall(videoEnabled) {
    const thread = await this.chat.getThreadByParticipants([this.contact]);
    if (thread?.id) {
      dispatcher.dispatch('chat', 'openCall', {
        thread: thread,
        videoEnabled: videoEnabled,
      });
    } else {
      // thread creation callback
      const startCallCallback = (thread) => {
        if (thread && thread.id) {
          dispatcher.dispatch('chat', 'openCall', {
            thread: thread,
            videoEnabled: videoEnabled,
          });
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
      };
      // thread doesn't exist yet, has to be created before starting a call
      let newThread = this.createNewThread();
      dispatcher.dispatch('chat', 'sendMessage', newThread, {
        createWithoutMsg: true,
        doneCallback: startCallCallback,
      });
    }
  }

  createNewThread() {
    const Thread = ThreadFactory(CurrentUserStore, ChatStore);
    return Thread.create({
      isNewChat: true,
      messagesInitied: true,
      startedBy: this.account.activeUser.id,
      participants: [this.contact],
      waitingForCreation: true,
      expandedChatSize: false
    });
  }
}
